import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Stack, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Divider, TextField, InputAdornment
} from '@mui/material';
import {
  ArrowBack, Refresh, Search, SmartToy, Summarize, Description,
  Warning, AccessTime
} from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

export default function AIAgentLogs() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const fetchLogs = useCallback(async () => {
    try {
      let url = `/admin/ai-logs?page=${page}`;
      const data = await apiRequest(url);
      setCalls(data.calls || []);
    } catch { setCalls([]); }
    setLoading(false);
  }, [page]);

  useEffect(() => { setLoading(true); fetchLogs(); }, [fetchLogs]);

  const filtered = calls.filter(c =>
    search === '' || (c.patient_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatDuration = (secs) => {
    if (!secs) return '—';
    if (secs < 60) return `${secs}s`;
    return `${Math.round(secs / 60)} min`;
  };

  const formatTime = (t) => {
    if (!t) return '—';
    return new Date(t).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/admin/control-room')} size="small"><ArrowBack /></IconButton>
              <Typography variant="h5" sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Vincula</Typography>
              <Chip label="Logs del Agente IA" size="small" variant="outlined" icon={<SmartToy sx={{ fontSize: 14 }} />} />
            </Stack>
            <Tooltip title="Refrescar"><IconButton onClick={fetchLogs}><Refresh /></IconButton></Tooltip>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <TextField size="small" placeholder="Buscar por paciente..." value={search}
            onChange={(e) => setSearch(e.target.value)} fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94A3B8' }} /></InputAdornment> }}
          />
        </Paper>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <SmartToy sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
              <Typography color="text.secondary">No hay llamadas con el agente IA</Typography>
            </Box>
          ) : (
            filtered.map((c, idx) => (
              <React.Fragment key={c.id}>
                {idx > 0 && <Divider />}
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between"
                  sx={{ p: 2, '&:hover': { bgcolor: '#F8FAFC' } }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <SmartToy sx={{ fontSize: 20, color: '#4F46E5' }} />
                    </Box>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight={600}>{c.patient_name}</Typography>
                        <Chip label={c.status} size="small" sx={{
                          height: 18, fontSize: 9,
                          bgcolor: c.status === 'completed' ? '#D1FAE5' : c.status === 'active' ? '#DBEAFE' : '#FEE2E2',
                          color: c.status === 'completed' ? '#059669' : c.status === 'active' ? '#2563EB' : '#DC2626',
                        }} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(c.started_at)} · {formatDuration(c.duration_seconds)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 } }}>
                    {c.has_summary && (
                      <Chip icon={<Summarize sx={{ fontSize: 12 }} />} label="Resumen" size="small"
                        sx={{ height: 22, fontSize: 10, bgcolor: '#D1FAE5', color: '#059669' }} />
                    )}
                    {c.has_transcript && (
                      <Chip icon={<Description sx={{ fontSize: 12 }} />} label="Transcripción" size="small"
                        sx={{ height: 22, fontSize: 10, bgcolor: '#DBEAFE', color: '#2563EB' }} />
                    )}
                    {c.alert_count > 0 && (
                      <Chip icon={<Warning sx={{ fontSize: 12 }} />} label={`${c.alert_count} alertas`} size="small"
                        sx={{ height: 22, fontSize: 10, bgcolor: '#FEE2E2', color: '#DC2626' }} />
                    )}
                  </Stack>
                </Stack>
              </React.Fragment>
            ))
          )}
        </Paper>

        {calls.length >= 30 && (
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
            {page > 0 && <Chip label="← Anterior" onClick={() => setPage(p => p - 1)} clickable />}
            <Chip label="Siguiente →" onClick={() => setPage(p => p + 1)} clickable />
          </Stack>
        )}
      </Container>
    </Box>
  );
}
